import React, { useState } from 'react'; // Import useState
import { Grid, Paper, Typography, Button, Popover, Stack } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrayKeysDisplay from './ArrayKeysDisplay';
import SingleValueKeysDisplay from './SingleValueKeysDisplay';
import axios from 'axios';
import { CONFIG } from '../../../../../../../config';
import { removeNullValueFromObject } from '../../../../../../../utility/object-related';

const API_HOST_CRM = CONFIG.modules['crm'].host;

/**
 * Displays shift details (user count, limits, allocation) and provides a popover
 * to view active filters used in the simple auto-assign algorithm.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.usersInShift - Array of user objects currently assigned to the shift.
 * @param {Array} props.usersAbsent - Array of user objects currently absent from the shift.
 * @param {number} props.shiftTotalLimit - The calculated total case limit for the shift.
 * @param {number} props.totalAllocated - The total number of cases currently allocated to users in the shift.
 * @param {boolean} props.isOverLimit - Flag indicating if the allocated count exceeds the shift limit.
 * @param {boolean} props.hasFilters - Flag indicating if there are any active filters applied.
 * @param {Object} props.filteredFilter - The filter object with null values removed.
 * @param {Map<string, boolean>} props.arrayKeys - Map of filter keys corresponding to array values.
 * @param {Map<string, boolean>} props.singleValueKeys - Map of filter keys corresponding to single values.
 * @param {Object} props.filter - The filter object.
 * @param {Object} props.shift - The shift object.
 * @param {Function} props.handleFetchCases - The function to fetch cases.
 * @returns {JSX.Element} The rendered component.
 */
const ShiftFilterDisplay = ({
  usersInShift,
  usersAbsent,
  shiftTotalLimit,
  totalAllocated,
  isOverLimit,
  hasFilters,
  filteredFilter,
  // Removed Popover props: filterPopoverId, openFilterPopover, anchorEl, handleFilterClick, handleFilterClose
  arrayKeys,
  singleValueKeys,
  filter,
  shift,
  handleFetchCases
}) => {

  // State for Popover moved inside
  const [anchorEl, setAnchorEl] = useState(null);


  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const openFilterPopover = Boolean(anchorEl);
  const filterPopoverId = openFilterPopover ? 'filter-popover' : undefined;


  return (
    <Grid container component={Paper} elevation={1} p={1} alignItems="flex-start">
      {/* Shift Details Paper */}
      <Grid item xs={12} md={8}> {/* Always allocate space, filter section handles its content */}
        {usersInShift && usersInShift.length > 0 ? (
          <Paper elevation={2} sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={2}> {/* Adjusted sm size for 5 items */}
                <Typography variant="body2" color="textSecondary">Users in Shift:</Typography>
                <Typography variant="body1" fontWeight="bold">{usersInShift.length}</Typography>
              </Grid>
              {/* <Grid item xs={12} sm={2}> 
                <Typography variant="body2" color="textSecondary">Users Absent:</Typography>
                <Typography variant="body1" fontWeight="bold">{usersAbsent?.length || 0}</Typography>
              </Grid> 
              */}
              <Grid item xs={12} sm={2}> {/* Adjusted sm size for 5 items */}
                <Typography variant="body2" color="textSecondary">Per User Limit:</Typography>
                <Typography variant="body1" fontWeight="bold">{shift?.number}</Typography> {/* Display shift.number */}
              </Grid>
              <Grid item xs={12} sm={2}> {/* Adjusted sm size for 5 items */}
                <Typography variant="body2" color="textSecondary">Shift Total Limit:</Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ color: isOverLimit ? 'error.main' : 'inherit' }}>
                  {shiftTotalLimit}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={2}> {/* Adjusted sm size for 5 items */}
                <Typography variant="body2" color="textSecondary">Currently Allocated:</Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ color: isOverLimit ? 'error.main' : 'inherit' }}>
                  {totalAllocated}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        ) : (
          <Typography variant="body1" color="textSecondary" sx={{ p: 2 }}>
            No users currently assigned to this shift.
          </Typography>
        )}
      </Grid>

      {/* Filter Popover Trigger */}
      {/* Filter Section */}
      <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center', pt: { xs: 1, md: 0 } }}>
        {hasFilters ? (
          // Active Filters Section
          <Stack direction={{ xs: 'row', sm: 'row', md: 'column' }} rowGap={1} columnGap={1}>
            <Button
              aria-describedby={filterPopoverId}
              variant="outlined"
              size="small"
              onClick={handleFilterClick}
              startIcon={<FilterListIcon />}
            >
              Selected Filters ({Object.values(filteredFilter).length})
            </Button>
            <Popover
              id={filterPopoverId}
              open={openFilterPopover}
              anchorEl={anchorEl}
              onClose={handleFilterClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              disableScrollLock={true}
              sx={{
                mb: 10
              }}
              marginThreshold={64} // Increase margin from viewport edge
            >
              <Stack sx={{ p: 2, minWidth: 300, maxHeight: '40vh', overflowY: 'auto' }} spacing={1}>
                <Typography variant="subtitle1" gutterBottom>Active Filters</Typography>
                <ArrayKeysDisplay arrayKeys={arrayKeys} filter={filter} />
                <SingleValueKeysDisplay singleValueKeys={singleValueKeys} filter={filter} x />
              </Stack>
            </Popover>
            <Button
              variant="outlined"
              size="small"
              onClick={handleFetchCases}
              startIcon={<FilterListIcon />} // Consider a different icon?
            >
              Fetch Cases
            </Button>
          </Stack>
        ) : (
          // No Filters Section
          <Stack direction={{ xs: 'row', sm: 'row', md: 'column' }} rowGap={1} columnGap={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', textAlign: { xs: 'left', md: 'right' }, width: '100%' }}>
              No filter selected
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled // Disable button
              startIcon={<FilterListIcon />}
            >
              Selected Filters (0)
            </Button>
            <Button
              variant="outlined"
              size="small"
              // Keep Fetch Cases enabled even without filters
              onClick={handleFetchCases} // Ensure onClick is present if needed when enabled
              startIcon={<FilterListIcon />} // Consider a different icon?
            >
              Fetch Cases
            </Button>
          </Stack>
        )}
      </Grid>
    </Grid>
  );
};

export default ShiftFilterDisplay;
